import api from "service/api"
import { NextPage } from "next"
import Head from "next/head"
import { useEffect } from "react"
import { useRouter } from "next/router"

import Layout from "components/common/Layout"
import withAuth, { AuthProps } from "components/decorator/WithAuth"
import ReadyToTakeLeapBanner from "components/LSRevamp/common/ReadyToTakeLeapBanner"
import ConnectWithCounsellor from "components/LSRevamp/CounsellorLandingPage/ConnectWithCounsellor/ConnectWithCounsellor"
import CounsellorGallerySection from "components/LSRevamp/CounsellorLandingPage/CounsellorGallerySection/CounsellorGallerySection"
import CounsellorRelatedStats from "components/LSRevamp/CounsellorLandingPage/CounsellorRelatedStats/index"
import HeroComponent from "components/LSRevamp/CounsellorLandingPage/HeroComponent/CounsellorsHeroComponent"
import { CounsellorSchema } from "DataMapper/CounsellorPage/CounsellorModel"
import { trackClick, trackPage } from "scripts/segment"
import { pageName } from "utils/events-utils"
import OrganizationSchema from "seo/OrganizationSchema"

interface CounsellorProps {
  counsellors: Array<CounsellorSchema>
}

const Counsellors: NextPage<CounsellorProps & AuthProps> = ({
  counsellors,
}) => {
  useEffect(() => {
    trackPage(pageName.CounsellorPage)
  }, [])

  const router = useRouter()

  return (
    <>
      <Head>
        <title>
          Study Abroad, Overseas Education Consultants, Talk to our Counsellor |
          LeapScholar
        </title>
        <meta
          property="og:image"
          content="https://leapassets.s3.ap-south-1.amazonaws.com/ielts-recording/1619511191304-logo@2x_(1)_(1).png"
        />
        <meta
          name="description"
          content="Get Free Study Abroad Counselling advice from our team of Expert Career Counsellors. We help students to Study Overseas in Canada, USA, UK, Australia and Ireland!"
        />
        <link
          rel="canonical"
          href={`${process.env.DOMAIN_NAME || "https://leapscholar.com"}${
            router.asPath?.split("#")[0]?.split("?")[0]
          }`}
        />

        <OrganizationSchema />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  item: {
                    "@id": "https://leapscholar.com",
                    name: "Home",
                  },
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  item: {
                    "@id": "https://leapscholar.com/counsellors",
                    name: "Counsellors",
                  },
                },
              ],
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Product",
              name: `Counsellors Page`,
              aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: "4.8",
              },
            }),
          }}
        />
      </Head>

      <Layout>
        <HeroComponent />
        <CounsellorGallerySection counsellors={counsellors} />
        <ConnectWithCounsellor />
        <CounsellorRelatedStats />
        <ReadyToTakeLeapBanner
          hasTwoButtons={false}
          header="Ready to take the Leap ?"
          buttonText="Talk to a Counsellor"
          content="5000+ students admitted with 97% success rate till now"
          trackingEvent={() => {
            trackClick(pageName.CounsellorPage, {
              contentName: "Talk to a Counsellor",
              widgetName: "Bottom Banner",
              widgetFormat: "Banner",
              contentFormat: "Button",
            })
          }}
        />
      </Layout>
    </>
  )
}

Counsellors.getInitialProps = async (ctx): Promise<any> => {
  try {
    const isServer = !!ctx.req
    const url = isServer
      ? `${process.env.SCHOLAR_URL}/counsellor`
      : `/api/scholarRoute?path=counsellor`

    const res = await api.get(url)
    if (res.data.success) {
      return {
        counsellors: res.data.data,
      }
    } else {
      return {
        counsellors: [],
      }
    }
  } catch (err: any) {
    return {
      counsellors: [],
    }
  }
}

export default withAuth(Counsellors, false)
